import { useEffect, useState } from "react";
import { Header, Footer } from "@/components/Header";
import { ProductCard } from "@/components/ProductCard";
import { api } from "@/lib/api";
import { X } from "lucide-react";

const FACETS = [
  { key: "product_type", label: "Type" },
  { key: "style", label: "Style" },
  { key: "color", label: "Color" },
  { key: "fabric", label: "Fabric" },
  { key: "occasion", label: "Occasion" },
];

export default function ProductsPage() {
  const [filters, setFilters] = useState({});
  const [active, setActive] = useState({});
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const PAGE = 48;

  useEffect(() => {
    api.get("/filters").then((r) => setFilters(r.data));
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = { ...Object.fromEntries(Object.entries(active).filter(([, v]) => v)), skip: 0, limit: PAGE };
    api.get("/products", { params }).then((r) => {
      setProducts(r.data.items || []);
      setTotal(r.data.total || 0);
      setLoading(false);
    });
  }, [active]);

  const loadMore = () => {
    setLoadingMore(true);
    const params = { ...Object.fromEntries(Object.entries(active).filter(([, v]) => v)), skip: products.length, limit: PAGE };
    api.get("/products", { params }).then((r) => {
      setProducts((prev) => [...prev, ...(r.data.items || [])]);
      setLoadingMore(false);
    });
  };

  const toggle = (key, val) =>
    setActive((a) => ({ ...a, [key]: a[key] === val ? undefined : val }));

  const activeCount = Object.values(active).filter(Boolean).length;

  return (
    <div className="min-h-screen bg-[#0B0B0C] relative z-[2]">
      <Header />
      <div className="max-w-[1500px] mx-auto px-6 lg:px-12 pt-32 pb-24">
        <p className="overline mb-3">The Catalogue</p>
        <h1 className="font-serif text-4xl lg:text-6xl font-light mb-12">Products</h1>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Filters */}
          <aside className="lg:col-span-3">
            <div className="lg:sticky lg:top-28 space-y-8">
              <div className="flex items-center justify-between">
                <span className="overline">Filters</span>
                {activeCount > 0 && (
                  <button
                    data-testid="clear-filters-button"
                    onClick={() => setActive({})}
                    className="flex items-center gap-1 text-xs text-[#D4AF37] hover:text-[#C5A059]"
                  >
                    <X size={12} /> Clear ({activeCount})
                  </button>
                )}
              </div>
              {FACETS.map((f) => (
                <div key={f.key}>
                  <p className="text-xs font-mono uppercase tracking-[0.15em] text-[#8E8E9A] mb-3">
                    {f.label}
                  </p>
                  <div className="flex flex-wrap gap-2 max-h-44 overflow-y-auto pr-1 filter-scroll">
                    {(filters[f.key] || []).map((val) => (
                      <button
                        key={val}
                        data-testid={`filter-${f.key}-${val}`}
                        onClick={() => toggle(f.key, val)}
                        className={`text-xs px-3 py-1.5 rounded-full border transition-all duration-300 ${
                          active[f.key] === val
                            ? "bg-[#D4AF37] text-black border-[#D4AF37]"
                            : "border-white/15 text-[#8E8E9A] hover:border-[#D4AF37] hover:text-[#F8F6F0]"
                        }`}
                      >
                        {val}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </aside>

          {/* Grid */}
          <div className="lg:col-span-9">
            <div className="flex justify-between items-center mb-8 border-b border-white/10 pb-4">
              <span className="font-mono text-xs text-[#8E8E9A]" data-testid="product-count">
                {total} PRODUCTS
              </span>
            </div>
            {loading ? (
              <div className="overline animate-pulse">Loading…</div>
            ) : products.length === 0 ? (
              <p className="text-[#8E8E9A] font-light">No products match these filters.</p>
            ) : (
              <>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                  {products.map((p, i) => (
                    <ProductCard key={p.id} product={p} index={i % 12} />
                  ))}
                </div>
                {products.length < total && (
                  <div className="flex justify-center mt-14">
                    <button
                      data-testid="load-more-button"
                      onClick={loadMore}
                      disabled={loadingMore}
                      className="px-10 py-3 rounded-full border border-[#D4AF37]/50 text-[#D4AF37] text-xs font-mono uppercase tracking-[0.2em] hover:bg-[#D4AF37] hover:text-black transition-all duration-300 disabled:opacity-50"
                    >
                      {loadingMore ? "Loading…" : `Load More (${total - products.length})`}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
